import { buildCollection, buildProperty, EntityAction } from "@firecms/core";

import {
  hasAnyRole,
  ONBOARDING_QUEUE_ROLES,
  ONBOARDING_VIEW_ROLES,
} from "./apple_delegated_onboarding.roles";
import {
  APPLE_DELEGATED_ONBOARDING_PATH,
  AppleDelegatedOnboardingEntity,
  BRAND_OWNERSHIP_TYPE_LABELS,
  BRAND_URL_TYPE_LABELS,
  LOCATION_OPENING_DAY_LABELS,
  LOCATION_STATUS_LABELS,
  ONBOARDING_STEP_LABELS,
  OnboardingStepName,
  PAYMENT_METHOD_LABELS,
} from "./apple_delegated_onboarding.types";
import {
  evaluateQueueGate,
  resolveDocumentEnvironment,
  stripFieldsTheFormMayNotWrite,
} from "./apple_delegated_onboarding.utils";
import { queueOnboardingStep } from "./apple_delegated_onboarding.write";
import { appleOnboardingGalleryPhotosCollection } from "./apple_delegated_onboarding_gallery";
import { stepStatusProperties } from "./apple_delegated_onboarding_properties";
import { appleOnboardingShowcaseRequestsCollection } from "./apple_delegated_onboarding_showcases";
import { AppleImageCropFieldProps } from "./apple_image_crop.types";
import { APPLE_LOCATION_ATTRIBUTE_ENUM_VALUES } from "./apple_location_content.types";
import { AppleAboutTextField } from "./AppleAboutTextField";
import { AppleImageCropField } from "./AppleImageCropField";
import {
  BrandAssetsStepChip,
  BrandStepChip,
  LocationCtaStepChip,
  LocationDetailsStepChip,
  LocationStepChip,
  MediaStepChip,
} from "./OnboardingStepChip";
import { OnboardingStepsView } from "./OnboardingStepsView";

/**
 * Builds the row-menu action for one step — the list-view twin of the gated button on the Steps tab.
 *
 * ⚠️ `EntityAction` in FireCMS 3.0.0-beta.14 has NO `isEnabled`/`disabled` predicate (only `name`,
 * `key`, `icon`, `onClick`, `collapsed`, `includeInForm`) — the same limitation documented at
 * `apple_grant_status.ts:28-31`. So the block cannot be declarative: it runs inside `onClick` and
 * explains itself in a snackbar rather than the action being absent. It calls the SAME
 * `evaluateQueueGate` the Steps tab calls, so a step can never be refused in one place and allowed
 * in the other.
 */
const buildQueueStepAction = (
  step: OnboardingStepName,
): EntityAction<Partial<AppleDelegatedOnboardingEntity>> => ({
  name: `Queue ${ONBOARDING_STEP_LABELS[step].toLowerCase()}`,
  key: `queue_${step}`,
  collapsed: true,
  onClick: async ({ entity, context }) => {
    const { snackbarController, authController, dataSource } = context;

    if (!hasAnyRole(authController, ONBOARDING_QUEUE_ROLES)) {
      snackbarController.open({
        type: "error",
        message: "You do not have permission to queue Apple onboarding steps.",
      });
      return;
    }

    const gate = evaluateQueueGate({
      target: step,
      step: entity.values[step],
      parent: entity.values,
      env: resolveDocumentEnvironment(entity.values),
    });

    if (!gate.allowed) {
      snackbarController.open({ type: "warning", message: gate.reason });
      return;
    }
    // Cautions are the things this console genuinely cannot decide. They do not block, but they must
    // be said — the Steps tab shows them next to the button; here they precede the write.
    for (const caution of gate.cautions) {
      snackbarController.open({ type: "info", message: caution });
    }

    await queueOnboardingStep(
      { dataSource, snackbarController },
      { path: entity.path, entityId: entity.id, queueTarget: step },
    );
  },
});

/**
 * `apple_delegated_onboarding` — the ONE document per DearDoc location that drives a practice onto
 * Apple Maps, and the surface that makes the backend's onboarding drain reachable at all.
 *
 * **The transport is this collection.** There is no HTTP call anywhere in it: staff fill a step's
 * `input`, press its button, and the button writes `submitState: 'QUEUED'`. A scheduled backend
 * drain claims that transactionally every 5 minutes, calls Apple, and writes the result back onto
 * the same document, which this screen re-renders from. Design §0 #1.
 *
 * **The document id is `${accountId}__${locationId}` — DearDoc's own ids, typed by staff.** The CMS
 * has no accounts or locations collection to pick from (design §3.6), so both are hand-entered and
 * validated only by shape. That is deliberate rather than a gap: a mistyped `accountId` resolves to
 * no active Apple grant and the step fails safely, which is the failure direction we want. Nobody
 * ever types an Apple `companyId` — the backend resolves it from the client's OAuth grant.
 *
 * ⚠️ **THE FIELD-OWNERSHIP BOUNDARY IS THE WHOLE CONTRACT**, and three mechanisms hold it here:
 *  1. every backend-owned property is `readOnly: true` (see `stepStatusProperties`);
 *  2. `onPreSave` reduces every form save to `input` + `companyId` per step, because a FireCMS form
 *     submits the whole document it loaded and would otherwise echo stale backend values back;
 *  3. every intent write goes through the single helper in `apple_delegated_onboarding.write.ts`,
 *     whose value objects are built by pure functions in `*.utils.ts`.
 *
 * ⚠️ **AND THE BACKEND DOES NOT TRUST ANY OF IT.** The Firestore rules let any authenticated
 * principal write here, and `services/auth.ts:25` admits any `@getdeardoc.com` address without
 * assigning roles, so the role gating below is a UI convention. The drain re-parses every input and
 * re-checks every readiness gate server-side before it calls Apple. That is an accepted, documented
 * cost (design §3.1), not something this file mitigates.
 */
export const appleDelegatedOnboardingCollection = buildCollection<
  Partial<AppleDelegatedOnboardingEntity>
>({
  id: APPLE_DELEGATED_ONBOARDING_PATH,
  path: APPLE_DELEGATED_ONBOARDING_PATH,
  name: "Apple Onboarding",
  singularName: "Apple Onboarding",
  description:
    "One record per DearDoc location being put on Apple Maps. Fill in each step's details, then queue it from the Steps tab. The client must have linked their Apple account first — check Apple Business Connect.",
  group: "Partners",
  icon: "map",
  textSearchEnabled: false,
  // ⚠️ The document id is `${accountId}__${locationId}` and must be typed by the person creating the
  // record — it is the dedup key, one onboarding document per location, and the CMS cannot derive it
  // before both fields exist. `customId: true` is what lets them enter it.
  customId: true,
  permissions: ({ authController }) => ({
    read: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    // Editing means filling in a step's `input` — harmless until someone queues it, which is gated
    // separately and more narrowly. Creating a record is likewise inert on its own.
    edit: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    create: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    // Deleting the record does NOT delete anything at Apple; it only destroys the audit trail of
    // what was created. Same roles that may queue.
    delete: hasAnyRole(authController, ONBOARDING_QUEUE_ROLES),
  }),
  // THE TWO SUBCOLLECTIONS — the surfaces that RECUR per location, and the only two things about a
  // practice's Apple presence that are not a one-off. Showcases recur over time (staff add showcase #2
  // next quarter); gallery photos recur by count (Apple permits up to a hundred per location). Both
  // are documents whose status fields sit at their ROOT, and both queue through the same gate as the
  // embedded steps above — see `stepFieldOf` for why "status at the root" is a field-path fact rather
  // than a special case in each surface.
  subcollections: [
    appleOnboardingShowcaseRequestsCollection,
    appleOnboardingGalleryPhotosCollection,
  ],
  entityViews: [
    {
      key: "onboarding_steps",
      name: "Steps",
      position: "start",
      Builder: OnboardingStepsView,
    },
  ],
  entityActions: [
    buildQueueStepAction("brand"),
    buildQueueStepAction("brandAssets"),
    buildQueueStepAction("media"),
    buildQueueStepAction("location"),
    buildQueueStepAction("locationCta"),
    buildQueueStepAction("locationDetails"),
  ],
  callbacks: {
    // The whole write boundary for the form, in one line. See `stripFieldsTheFormMayNotWrite` for
    // why `readOnly: true` alone is not enough.
    onPreSave: ({ values }) => stripFieldsTheFormMayNotWrite(values),
  },
  // ⚠️ Must list EVERY property key, not just the table columns. `getFormFieldKeys` filters the
  // entity form through this same array, so an omitted key disappears from the DETAIL FORM too —
  // which here would silently remove a whole step. Table trimming stays `hideFromCollection`'s job
  // (`apple_grant_status.ts:130-133`).
  propertiesOrder: [
    "brandStatus",
    "brandAssetsStatus",
    "mediaStatus",
    "locationStatus",
    "locationCtaStatus",
    "accountId",
    "locationId",
    "salesforce_id",
    "notes",
    "createdAt",
    "updatedAt",
    "brand",
    "brandAssets",
    "media",
    "location",
    "locationCta",
  ],
  properties: {
    accountId: buildProperty({
      name: "DearDoc Account ID",
      description:
        "The first half of this record's ID. Lives in the dashboard project, so nothing here can verify it — a wrong value fails as “no active Apple grant”, which is the safe direction.",
      dataType: "string",
      validation: { required: true },
    }),
    locationId: buildProperty({
      name: "DearDoc Location ID",
      description:
        "⚠️ DearDoc's location id, NOT Apple's. The second half of this record's ID. Apple's location id is minted by Apple and appears in the location step's Apple result.",
      dataType: "string",
      validation: { required: true },
    }),
    salesforce_id: buildProperty({
      name: "Salesforce ID",
      description: "Carried for search only. Never used to resolve the client's Apple grant.",
      dataType: "string",
    }),
    notes: buildProperty({
      name: "Notes",
      description: "Free text for staff. Never read by the backend.",
      dataType: "string",
      multiline: true,
    }),
    createdAt: buildProperty({
      name: "Created At",
      readOnly: true,
      hideFromCollection: true,
      dataType: "date",
      autoValue: "on_create",
    }),
    updatedAt: buildProperty({
      name: "Updated At (backend)",
      readOnly: true,
      hideFromCollection: true,
      dataType: "date",
    }),

    brand: buildProperty({
      name: ONBOARDING_STEP_LABELS.brand,
      description:
        "Resolve-or-create a brand under the client's own Apple company. The front of the chain: Apple refuses to create a location until this brand's state is PUBLISHED, which is an Apple-side wait of days.",
      dataType: "map",
      expanded: false,
      properties: {
        input: buildProperty({
          name: "Brand details",
          description: "The staff-supplied brand intent, in DearDoc's vocabulary.",
          dataType: "map",
          properties: {
            name: buildProperty({
              name: "Brand name",
              description:
                "Required. Nothing is sent to Apple without it. This is the practice or group name as clients know it — not an individual doctor's name, which belongs on the location step.",
              dataType: "string",
            }),
            practiceType: buildProperty({
              name: "Practice type",
              description:
                "Deliberately free text, not a dropdown: an unmapped specialty must reach the backend's brand mapper and come back as an actionable gap, rather than being silently rejected here.",
              dataType: "string",
            }),
            categories: buildProperty({
              name: "Apple categories",
              description:
                "Optional override. Leave EMPTY normally — the practice type above is mapped to an Apple category for you. Fill this in only when an engineer gives you exact Apple category ids; a wrong id here replaces the mapped one.",
              dataType: "array",
              of: { dataType: "string" },
            }),
            countryCode: buildProperty({
              name: "Country code",
              description:
                'ISO-3166 two-letter code for the brand\'s home country — "US", not "USA" and not "United States". ⚠️ Anything other than exactly two letters is dropped rather than corrected.',
              dataType: "string",
            }),
            website: buildProperty({
              name: "Website",
              description:
                "Optional. Full URL including https:// (http:// is also accepted). Sent as the brand's homepage, in addition to any rows below. Anything that is not a valid URL is dropped silently.",
              dataType: "string",
            }),
            urls: buildProperty({
              name: "URLs",
              description:
                "⚠️ Every row needs BOTH a full URL (Apple validates the format) and a type. A half-filled row rejects the entire brand step, so remove a row rather than leaving it blank.",
              dataType: "array",
              of: {
                dataType: "map",
                properties: {
                  url: buildProperty({
                    name: "URL",
                    description:
                      "Full URL including https:// — Apple requires the scheme, so a bare domain is rejected.",
                    dataType: "string",
                  }),
                  type: buildProperty({
                    name: "Type",
                    description:
                      "What this URL is. Pick the practice's own website as HOMEPAGE and social pages as their own types — Apple expects the official website to be the HOMEPAGE one.",
                    dataType: "string",
                    enumValues: BRAND_URL_TYPE_LABELS,
                  }),
                },
              },
            }),
            ownershipType: buildProperty({
              name: "Ownership type",
              description:
                "Optional. Leave blank unless you know it differs — a blank field uses the backend's default rather than being saved empty.",
              dataType: "string",
              enumValues: BRAND_OWNERSHIP_TYPE_LABELS,
            }),
            locale: buildProperty({
              name: "Locale",
              description:
                'Optional. Language-and-region tag for the brand name, e.g. "en-US" — which is what is used when this is left blank, so normally leave it blank.',
              dataType: "string",
            }),
            partnersBrandId: buildProperty({
              name: "Partner brand key",
              description:
                "Optional stable key used to match an existing brand instead of creating a second one. Leave empty unless an engineer asked for it — an empty field is removed from the document rather than saved as blank, which the backend requires.",
              dataType: "string",
            }),
          },
        }),
        ...stepStatusProperties({ resourceNoun: "brand" }),
      },
    }),

    brandAssets: buildProperty({
      name: ONBOARDING_STEP_LABELS.brandAssets,
      description:
        "The cover photo and place-card logo, which hang off the brand rather than the location. ⚠️ BOTH are required: Apple shows a practice's branding as a SET, so one alone spends days in Apple review and then displays nothing. Runs in parallel with the brand's own review — assets attach to a brand that merely EXISTS, so this does not wait for PUBLISHED.",
      dataType: "map",
      expanded: false,
      properties: {
        input: buildProperty({
          name: "Cover photo & logo",
          description: "The staff-supplied branding. Both images, plus one alt text used for both.",
          dataType: "map",
          properties: {
            coverPhoto: buildProperty({
              name: "Cover photo",
              description:
                "Apple's rules are exact: at least 1600x1040, at most 4864 per side, and 1.54:1 (the tolerance is 1.5380–1.5402, so a 1920x1250 photo at 1.536:1 is REFUSED). Attach a JPEG or PNG and frame the crop — the cropper targets 1600x1040's own exact shape, so the result always lands inside Apple's window. ⚠️ Apple also accepts HEIC here, but this console does not: it cannot convert one, so a HEIC must be exported to JPEG or PNG first. No SVG. The backend re-checks all of it from the file's own header before any Apple call.",
              dataType: "map",
              // ⚠️ The cropper, aspect-locked to this surface. See `AppleImageCropField` for why a
              // plain `storage:` property cannot do this (it uploads on file-select, so there is no
              // point at which a crop fits) and `apple_image_crop.utils.ts` for the numbers.
              Field: AppleImageCropField,
              customProps: { surface: "coverPhoto" } satisfies AppleImageCropFieldProps,
              properties: {
                fileName: buildProperty({
                  name: "File name",
                  description: "What Apple is told the file is called. Required.",
                  dataType: "string",
                }),
                imageUrl: buildProperty({
                  name: "Image URL",
                  description:
                    "A DearDoc storage URL the backend downloads before uploading. Required. ⚠️ Deliberately NOT validated as a URL here: a bad value must surface as an actionable BRAND_ASSET_IMAGE_UNUSABLE failure with a message, not as a document the drain silently skips.",
                  dataType: "string",
                }),
              },
            }),
            placecardLogo: buildProperty({
              name: "Place-card logo",
              description:
                'EXACTLY 1024x1024, 1:1. JPEG, PNG — or SVG, which is the one format accepted here and not for the cover photo, and which Apple arguably prefers for a logo. ⚠️ An SVG is uploaded AS-IS with no crop step and no dimension check: a vector logo has no fixed pixel size, so "exactly 1024x1024" is not a meaningful statement about it, and Apple\'s own pre-validate is the only check on it. A JPEG or PNG is cropped to 1:1 and then scaled to exactly 1024x1024, so the source must be at least 1024x1024 — a smaller one is refused rather than enlarged. ⚠️ Apple accepts HEIC here too; this console does not, because it cannot convert one.',
              dataType: "map",
              // ⚠️ Same cropper, different surface — and this is the one that also RESIZES, because
              // "exactly 1024x1024" cannot be reached by cropping alone. SVG bypasses it entirely.
              Field: AppleImageCropField,
              customProps: { surface: "placecardLogo" } satisfies AppleImageCropFieldProps,
              properties: {
                fileName: buildProperty({
                  name: "File name",
                  description: "What Apple is told the file is called. Required.",
                  dataType: "string",
                }),
                imageUrl: buildProperty({
                  name: "Image URL",
                  description:
                    "A DearDoc storage URL the backend downloads before uploading. Required.",
                  dataType: "string",
                }),
              },
            }),
            altText: buildProperty({
              name: "Alt text (both images)",
              description:
                "Optional, up to 255 characters. ⚠️ ONE field for BOTH images, matching the backend: Apple's guidance is one concise sentence describing the business, which is the same sentence for a cover photo and a logo. ⚠️ UPDATE (2026-08-28): this used to say \"Apple requires alt text, so leaving it empty makes the step refuse before any Apple call\" — that was WRONG. Apple marks only the image and the intent required on a brand asset; leaving this empty sends the images without alt text and does not refuse anything. Worth filling in anyway: it is what a screen-reader user hears in place of the practice's branding.",
              dataType: "string",
              validation: { max: 255 },
            }),
            locale: buildProperty({
              name: "Alt-text locale",
              description:
                "BCP-47, e.g. en-US. Optional — the backend defaults it to en-US. Apple marks both the text and its locale required on each alt-text entry, which is why there is a default rather than an omission.",
              dataType: "string",
            }),
            // ⚠️ THERE IS DELIBERATELY NO CAPTION FIELD, and one must not be added. `captions` does
            // not exist on an Apple brand asset — a BrandAssetCaptionRequestV3 shape appears in the
            // OpenAPI document, but no brand-asset endpoint accepts it. Adding one here would collect
            // staff effort the backend has nowhere to send.
          },
        }),
        ...stepStatusProperties({ resourceNoun: "cover photo and logo" }),
      },
    }),

    media: buildProperty({
      name: ONBOARDING_STEP_LABELS.media,
      description:
        "Uploads the practice image under the client's Apple company. Runs in parallel with the brand — it is the one step with no cross-step precondition. Apple reviews images asynchronously, typically a few business days.",
      dataType: "map",
      expanded: false,
      properties: {
        input: buildProperty({
          name: "Image",
          description:
            "Square (1:1), between 492 and 4864 pixels per side — the showcase step turns this upload into an Apple showcase CREATIVE, and that is where 1:1 comes from. JPEG or PNG; attach one and frame the crop. ⚠️ THE CROP HERE IS THE ONLY GEOMETRY CHECK BEFORE THE UPLOAD. Unlike the cover photo and logo, the backend does NOT measure this image: `DelegatedMediaService` takes an optional `targetSurface` and the onboarding media step deliberately omits it, because an uploaded image is company-scoped and could later be attached to a gallery (up to 2.2:1) instead of a creative. So a non-square image here is not refused by the drain — Apple's own review, days later, is the next thing that would object.",
          dataType: "map",
          // ⚠️ Same cropper, locked to 1:1. This surface is the one where the CMS check is not
          // duplicated server-side — see the description above.
          Field: AppleImageCropField,
          customProps: { surface: "showcaseImage" } satisfies AppleImageCropFieldProps,
          properties: {
            fileName: buildProperty({
              name: "File name",
              description: "What Apple is told the file is called. Required.",
              dataType: "string",
            }),
            imageUrl: buildProperty({
              name: "Image URL",
              description:
                "A DearDoc storage URL the backend downloads before uploading. Required. ⚠️ Deliberately NOT validated as a URL: a bad value must surface as an actionable MEDIA_UNUSABLE failure with a message, not as a document the drain silently skips.",
              dataType: "string",
            }),
          },
        }),
        ...stepStatusProperties({ resourceNoun: "image" }),
      },
    }),

    location: buildProperty({
      name: ONBOARDING_STEP_LABELS.location,
      description:
        "Creates the location under the client's Apple company, carrying the booking action link. ⚠️ Apple refuses this until the brand is PUBLISHED, and the create is NOT idempotent — a retry after an unclear outcome duplicates a real location.",
      dataType: "map",
      expanded: false,
      properties: {
        input: buildProperty({
          name: "Location details",
          dataType: "map",
          properties: {
            name: buildProperty({
              name: "Location name",
              description:
                'Required. Nothing is sent to Apple without it. For a US health professional Apple expects "Dr. Homer Simpson, DDS, MPH" — optional title, first and last name, then accreditation. ⚠️ Do NOT put a suite, room, office or unit number in the name; that belongs in the address, and a name containing one is flagged as a data-quality note on the result. Do not fuse the practice/brand name into a medical professional\'s name either — Apple wants the brand kept out of the primary name for health professionals.',
              dataType: "string",
            }),
            practiceType: buildProperty({
              name: "Practice type",
              description:
                "Free text for the same reason as the brand step's — an unmapped value must reach the mapper as an actionable gap.",
              dataType: "string",
            }),
            status: buildProperty({
              name: "Open or closed",
              description:
                "Optional. Clearing it removes the field entirely rather than saving it blank — which is what the backend needs, because it accepts this key being absent but rejects an empty one.",
              dataType: "string",
              enumValues: LOCATION_STATUS_LABELS,
            }),
            address: buildProperty({
              name: "Address",
              dataType: "map",
              properties: {
                street: buildProperty({
                  name: "Street",
                  description:
                    "The single street line, house number included. ⚠️ Must be premises a member of the public can physically visit — a P.O. Box is refused outright and the location is never sent to Apple.",
                  dataType: "string",
                }),
                city: buildProperty({
                  name: "City",
                  description:
                    "Required for a structured address: Apple needs street, city, state, postal code AND country together. Leave any one blank and the whole structured address is dropped, leaving only the map pin.",
                  dataType: "string",
                }),
                state: buildProperty({
                  name: "State",
                  description:
                    "Two-letter state code, e.g. NY. Part of the all-or-nothing structured address.",
                  dataType: "string",
                }),
                postalCode: buildProperty({
                  name: "Postal code",
                  description:
                    "ZIP as written, e.g. 10011. Part of the all-or-nothing structured address.",
                  dataType: "string",
                }),
                country: buildProperty({
                  name: "Country",
                  description:
                    'ISO-3166 two-letter code — "US", not "USA" and not "United States". ⚠️ Anything other than exactly two letters is silently rejected and the whole structured address is dropped, so this format is load-bearing.',
                  dataType: "string",
                }),
                fullAddress: buildProperty({
                  name: "Full address",
                  description:
                    "Optional. Leave blank and it is composed from the fields above. ⚠️ If you do fill it in, it OVERRIDES that composed line and is sent to Apple exactly as typed with no checking — so it must match the address above and, like Street, must not be a P.O. Box.",
                  dataType: "string",
                }),
              },
            }),
            coordinates: buildProperty({
              name: "Coordinates",
              dataType: "map",
              properties: {
                latitude: buildProperty({
                  name: "Latitude",
                  description:
                    "Optional decimal degrees, -90 to 90. Sets the map pin; leave both coordinates blank and Apple places the pin from the address instead. Exactly 0 is treated as a missing value, not as a real position.",
                  dataType: "number",
                }),
                longitude: buildProperty({
                  name: "Longitude",
                  description:
                    "Optional decimal degrees, -180 to 180. Out-of-range or 0/0 coordinates are dropped and noted on the result rather than sent.",
                  dataType: "number",
                }),
              },
            }),
            openingHours: buildProperty({
              name: "Opening hours",
              dataType: "array",
              of: {
                dataType: "map",
                properties: {
                  day: buildProperty({
                    name: "Day",
                    dataType: "string",
                    enumValues: LOCATION_OPENING_DAY_LABELS,
                  }),
                  opensAt: buildProperty({
                    name: "Opens at",
                    description:
                      'Opening time on the 24-hour clock, HH:MM — "17:00", not "5pm". Open 24 hours that day? Enter 00:00 here and 24:00 in Closes at. ⚠️ Leave opening hours EMPTY altogether for an appointment-only practice; Apple prefers no hours to wrong hours.',
                    dataType: "string",
                  }),
                  closesAt: buildProperty({
                    name: "Closes at",
                    description:
                      "Closing time on the 24-hour clock, HH:MM. Past midnight is fine — 22:00 to 00:30. ⚠️ Apple rejects a day whose opening and closing times are identical, so that day is dropped and the place card shows the practice as CLOSED then. Enter the real hours, or 00:00 to 24:00 for a 24-hour day.",
                    dataType: "string",
                  }),
                },
              },
            }),
            // ⚠️ The 5-business-day lead in the copy below is APPLE'S REQUIREMENT, not our caution:
            // special hours "should be scheduled at least 5 business days in advance"
            // (`AppleMapsPlaceCard.rtf:197`, byte-identical `ApplePlaceCard.rtf`). It is a DEADLINE ON
            // US, distinct from the ~1-week card-visibility fact also stated there — the first governs
            // when staff must enter it, the second when a patient can see it. Nothing validates the
            // lead time yet, so the hint is currently the only thing that tells a staff member.
            specialHours: buildProperty({
              name: "Holiday & seasonal hours",
              description:
                "For holidays and seasonal hours ONLY — hours that differ from the regular week above. ⚠️ NOT for a temporary closure: a practice shut for refurbishment or a move is set with Open/closed above, not here. ⚠️ These appear on the place card only about a WEEK ahead, so an entry for December 24th shows up around December 18th and not before — that is Apple, not a fault. ⚠️ Enter each one at least 5 BUSINESS DAYS before its first day — Apple asks for that much notice to schedule it, so a closure typed in three days beforehand may simply never take effect. Nothing here blocks a late entry, so this one is on you to watch. Leave this empty for practices that keep their normal hours all year.",
              dataType: "array",
              of: {
                dataType: "map",
                properties: {
                  startDate: buildProperty({
                    name: "First day",
                    description:
                      "⚠️ A STRING, not a date picker: the backend parses these with a string schema and a Firestore timestamp would fail that parse. Calendar date only, YYYY-MM-DD — e.g. 2026-12-24. This day IS included. A date that does not exist (2026-02-31) is refused with the row named, so check it.",
                    dataType: "string",
                  }),
                  endDate: buildProperty({
                    name: "Last day (INCLUDED)",
                    description:
                      "Same YYYY-MM-DD format, and this day IS included — unlike the showcase end date, which is exclusive. For a single day, repeat the first day. ⚠️ A closure longer than one day belongs in ONE row with a range here, not one row per day — Apple asks for the range, and a run of consecutive single-day closures is flagged on the result.",
                    dataType: "string",
                  }),
                  closed: buildProperty({
                    name: "Closed these days",
                    description:
                      "Tick when the practice is shut for the whole period. Ticked, the hours below are IGNORED and need not be filled in. Un-ticked, hours ARE required — a row that says the practice is open but states no hours is refused and nothing is sent to Apple.",
                    dataType: "boolean",
                  }),
                  hoursByDay: buildProperty({
                    name: "Hours on those days",
                    description:
                      "Only needed when “Closed these days” is NOT ticked. Same 24-hour HH:MM format as the regular week — one row per day the practice is open during this period.",
                    dataType: "array",
                    of: {
                      dataType: "map",
                      properties: {
                        day: buildProperty({
                          name: "Day",
                          dataType: "string",
                          enumValues: LOCATION_OPENING_DAY_LABELS,
                        }),
                        opensAt: buildProperty({
                          name: "Opens at",
                          description:
                            'HH:MM on the 24-hour clock — "17:00", not "5pm". Open 24 hours? Enter 00:00 here and 24:00 in Closes at.',
                          dataType: "string",
                        }),
                        closesAt: buildProperty({
                          name: "Closes at",
                          description:
                            "HH:MM on the 24-hour clock. ⚠️ Identical opening and closing times are rejected by Apple, so that day is dropped and shows as CLOSED — enter the real hours, or 00:00 to 24:00 for a full day.",
                          dataType: "string",
                        }),
                      },
                    },
                  }),
                  descriptions: buildProperty({
                    name: "Reason shown on the place card",
                    description:
                      "Optional, and one row per language. ⚠️ When “Closed these days” is ticked, Apple requires this to say only WHY the practice is closed — “Closed Christmas Day”. It must NOT carry a phone number, an alternative contact, or when to try again.",
                    dataType: "array",
                    of: {
                      dataType: "map",
                      properties: {
                        text: buildProperty({
                          name: "Reason",
                          description:
                            'Short and specific, e.g. "Closed Christmas Day" or "Early closing Christmas Eve". A row with no text is dropped rather than failing the step.',
                          dataType: "string",
                        }),
                        locale: buildProperty({
                          name: "Language",
                          description:
                            'The language the reason is written in, e.g. "en-US". Left blank it uses the location Locale below, so normally leave it blank; set it only for a second language.',
                          dataType: "string",
                        }),
                      },
                    },
                  }),
                },
              },
            }),
            paymentMethods: buildProperty({
              name: "Payment methods",
              description:
                "Shown under “Good to Know” on the place card. ⚠️ Select only what the practice GENUINELY accepts — each one is a published claim about a real clinic, and an unticked box tells Apple nothing rather than saying they refuse it. FINANCING (payment plans) and INVOICE (bill me) are the two that matter most to medical practices and are worth asking about. ⚠️ This list REPLACES whatever is on Apple, it does not add to it — so if the client set payment methods in Business Connect themselves, tick those here too or they go away.",
              dataType: "array",
              of: {
                dataType: "string",
                enumValues: PAYMENT_METHOD_LABELS,
              },
            }),
            phone: buildProperty({
              name: "Phone",
              description:
                'Optional. Apple wants it in international form, beginning "+" with the country code and no spaces or punctuation — e.g. +12225551212. It is sent as the practice\'s primary voice number exactly as typed; nothing here reformats it.',
              dataType: "string",
            }),
            website: buildProperty({
              name: "Website",
              description:
                "Optional. Full URL including https:// (http:// is also accepted). Becomes the place card's website link. ⚠️ Anything that is not a valid URL is dropped silently and noted on the result — no website link appears.",
              dataType: "string",
            }),
            bookingUrl: buildProperty({
              name: "Booking URL",
              description:
                "⚠️ THE WHOLE POINT OF THE FEATURE. Without it the location is created with no Schedule quicklink — a valid place card with no booking button — and the result records “bookingOmitted”.",
              dataType: "string",
            }),
            locale: buildProperty({
              name: "Locale",
              description:
                'Optional. Language-and-region tag for the name and address, e.g. "en-US" — which is what is used when this is left blank, so normally leave it blank.',
              dataType: "string",
            }),
          },
        }),
        ...stepStatusProperties({ resourceNoun: "location" }),
      },
    }),

    locationCta: buildProperty({
      name: ONBOARDING_STEP_LABELS.locationCta,
      description:
        "Step 5 of Apple's booking-button process, and the only step here whose Apple write is an UPDATE rather than a create — so nothing can be duplicated by re-running it. Apple requires the location to be PUBLISHED first, which means the client has finished verifying it. The place card's “About the place” text and “Good to Know” amenities ride this same write.",
      dataType: "map",
      expanded: false,
      properties: {
        input: buildProperty({
          name: "Place card content",
          dataType: "map",
          properties: {
            callToAction: buildProperty({
              name: "Call to action",
              description:
                "⚠️ Free text, not a dropdown, and that is deliberate: Apple's available values are per-location and only knowable from its own Get-CTAs read, which happens when this step runs. Leave EMPTY for SCHEDULE — the booking button this feature exists for. An empty field is removed from the document rather than saved as blank, which is what lets the backend apply that default; a blank saved value would reject the whole step.",
              dataType: "string",
            }),
            about: buildProperty({
              name: "About the place",
              description:
                "The description shown under “About” on the Apple Maps place card. Leave the text empty to send none — the client's existing description is then left exactly as it is, not cleared.",
              dataType: "map",
              properties: {
                text: buildProperty({
                  name: "Description",
                  // ⚠️ The custom field is the character counter. Apple caps this at 500 and refuses
                  // the whole step over 501, and nothing else in this package can catch that before
                  // the backend does. See `AppleAboutTextField`.
                  dataType: "string",
                  Field: AppleAboutTextField,
                }),
                locale: buildProperty({
                  name: "Language",
                  description:
                    "The language the description above is written in — Apple requires it whenever there IS a description. Enter “en” for English; change it if the text is not in English, because this is what Apple labels the text as on the place card.",
                  dataType: "string",
                  // ⚠️ DELIBERATELY NO `defaultValue`, and it is not a papercut. A default here is the
                  // ONLY value in the `about` map on an untouched record, which keeps the map alive
                  // through `pruneFormValue` and stores `{ locale: "en" }` with no text — and the
                  // backend would then refuse the ENTIRE booking-button step for every record whose
                  // description nobody filled in. Same reason the brand-asset `locale` above carries
                  // none. The backend now also normalises a text-less `about` away, so this is one of
                  // two independent guards rather than the only one.
                }),
              },
            }),
            locationAttributes: buildProperty({
              name: "Good to Know",
              description:
                "Amenities shown under “Good to Know” on the place card. ⚠️ Tick a box only when the practice HAS the amenity. An unticked box tells Apple nothing — it does NOT say the practice lacks it — so leaving one blank is always safe, and ticking one you are unsure of publishes a claim about a real clinic. Un-ticking a box you previously saved DOES remove that claim from Apple Maps on the next run — so if something changes at the practice (a ramp removed, parking closed), untick it and re-queue this step. Unticking removes the amenity row for a path DearDoc previously saved — so if the client has since ticked that same amenity in Business Connect themselves, it goes too (Apple keeps one row per amenity, not one per author). Amenities DearDoc never saved are never touched.",
              dataType: "array",
              of: {
                dataType: "string",
                // Apple's own labels, prefixed with their group. See `apple_location_content.types`
                // for why this list is a mirror of the backend's generated vocabulary.
                enumValues: APPLE_LOCATION_ATTRIBUTE_ENUM_VALUES,
              },
            }),
          },
        }),
        ...stepStatusProperties({ resourceNoun: "place-card booking button" }),
      },
    }),

    locationDetails: buildProperty({
      name: ONBOARDING_STEP_LABELS.locationDetails,
      description:
        "Sends the holiday hours and payment methods ALREADY SAVED on the Location section above to a place card that already exists — it changes nothing here, it publishes what is here. ⚠️ There are no fields on this step: edit the details up in Location, save, then queue this. ⚠️ This is the ONE step you are meant to queue again and again — every December for next year's holidays, and whenever the practice changes what it accepts at the desk. It is not the booking button; that is its own step above.",
      dataType: "map",
      expanded: false,
      properties: {
        // ⚠️ NO `input` PROPERTY, AND THAT IS THE POINT. The two fields this step sends are edited on
        // the LOCATION section — one place to edit, two verbs over it (create, then update). A second
        // copy here would look authoritative and would silently disagree with the first the moment
        // anyone edited the wrong one. The backend's step carries an empty `input` for the same reason
        // and says so in its schema.
        //
        // ⚠️ A reader looking for this step's data will not find it under this step. That asymmetry is
        // real — every other step here owns its input — and the description above is where a staff
        // member is told, rather than left to discover it by finding an empty panel.
        ...stepStatusProperties({ resourceNoun: "holiday hours and payment method" }),
      },
    }),
  },
  // One chip per step in the list, so "where is this practice up to?" is answerable without opening
  // anything. Placed before every property so they are the first columns.
  additionalFields: [
    {
      key: "brandStatus",
      name: "Brand",
      dependencies: ["brand"],
      Builder: BrandStepChip,
    },
    {
      key: "brandAssetsStatus",
      name: "Cover & logo",
      dependencies: ["brandAssets"],
      Builder: BrandAssetsStepChip,
    },
    {
      key: "mediaStatus",
      name: "Image",
      dependencies: ["media"],
      Builder: MediaStepChip,
    },
    {
      key: "locationStatus",
      name: "Location",
      dependencies: ["location"],
      Builder: LocationStepChip,
    },
    {
      key: "locationCtaStatus",
      name: "Booking button",
      dependencies: ["locationCta"],
      Builder: LocationCtaStepChip,
    },
    {
      key: "locationDetailsStatus",
      name: "Details update",
      // ⚠️ ALSO DEPENDS ON `location`, because the step's status is its own but everything it SENDS
      // lives on the location step — so a chip that only watched `locationDetails` would not re-render
      // when the holiday hours it is about to publish were edited.
      dependencies: ["locationDetails", "location"],
      Builder: LocationDetailsStepChip,
    },
  ],
});
